/**
 * Deterministic natural-language time parser for the QuickWhen picker (kms
 * `app/components/QuickWhen/parseWhen.ts`, ported 1:1 — owner-added, no LLM, no dependency).
 * Handles the common reminder phrases: "today", "later today", "tomorrow", "tomorrow 9",
 * "in 3 days", "in 2 hours", "next monday", "fri 14:00", "mon 9am", "every monday", "daily",
 * "weekly". Returns null when it can't parse, so the preview simply stays empty.
 *
 * Its behaviour IS the contract (W2 §A.2) — do not "improve" the regexes without re-reading
 * the ten-step evaluation order there.
 */

export type Recurrence = "DAILY" | "WEEKLY" | "MONTHLY";

export interface ParsedWhen {
  date: Date;
  recurrence: Recurrence | null;
}

const WEEKDAYS: Record<string, number> = {
  sunday: 0,
  sun: 0,
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  tues: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  thur: 4,
  thurs: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
};

const DEFAULT_HOUR = 9;

function atTime(base: Date, hour: number, minute: number): Date {
  const d = new Date(base);
  d.setHours(hour, minute, 0, 0);
  return d;
}

/** Extract an explicit time ("14:00", "2pm", "9am") → {hour, minute} or null. */
function extractTime(text: string): { hour: number; minute: number } | null {
  // 14:00 / 9:30
  let m = text.match(/\b(\d{1,2}):(\d{2})\s*(am|pm)?\b/);
  if (m) {
    let hour = Number.parseInt(m[1] ?? "0", 10);
    const minute = Number.parseInt(m[2] ?? "0", 10);
    const mer = m[3];
    if (mer === "pm" && hour < 12) hour += 12;
    if (mer === "am" && hour === 12) hour = 0;
    if (hour <= 23 && minute <= 59) return { hour, minute };
  }
  // 2pm / 9am
  m = text.match(/\b(\d{1,2})\s*(am|pm)\b/);
  if (m) {
    let hour = Number.parseInt(m[1] ?? "0", 10);
    const mer = m[2];
    if (mer === "pm" && hour < 12) hour += 12;
    if (mer === "am" && hour === 12) hour = 0;
    if (hour <= 23) return { hour, minute: 0 };
  }
  return null;
}

function nextWeekday(now: Date, weekday: number, forceNext: boolean): Date {
  const d = new Date(now);
  const diff = (weekday - d.getDay() + 7) % 7;
  const add = diff === 0 && forceNext ? 7 : diff;
  d.setDate(d.getDate() + add);
  return d;
}

export function parseWhen(input: string, now: Date = new Date()): ParsedWhen | null {
  const text = input.trim().toLowerCase();
  if (!text) {
    return null;
  }

  const time = extractTime(text);
  let recurrence: Recurrence | null = null;

  // Recurrence phrases.
  if (/\b(every day|daily)\b/.test(text)) recurrence = "DAILY";
  else if (/\b(every week|weekly)\b/.test(text)) recurrence = "WEEKLY";
  else if (/\b(every month|monthly)\b/.test(text)) recurrence = "MONTHLY";

  const everyWeekday = text.match(/\bevery\s+([a-z]+)\b/);
  const everyWeekdayNum = everyWeekday?.[1] ? WEEKDAYS[everyWeekday[1]] : undefined;
  if (everyWeekday && everyWeekdayNum !== undefined) {
    recurrence = "WEEKLY";
    const date = atTime(
      nextWeekday(now, everyWeekdayNum, false),
      time?.hour ?? DEFAULT_HOUR,
      time?.minute ?? 0,
    );
    return { date, recurrence };
  }

  // "in N days/hours/weeks/minutes".
  const rel = text.match(/\bin\s+(\d+)\s*(min|minute|minutes|hour|hours|day|days|week|weeks)\b/);
  if (rel) {
    const n = Number.parseInt(rel[1] ?? "0", 10);
    const unit = rel[2] ?? "";
    const d = new Date(now);
    if (unit.startsWith("min")) d.setMinutes(d.getMinutes() + n);
    else if (unit.startsWith("hour")) d.setHours(d.getHours() + n);
    else if (unit.startsWith("day")) d.setDate(d.getDate() + n);
    else if (unit.startsWith("week")) d.setDate(d.getDate() + n * 7);
    return { date: d, recurrence };
  }

  if (/\blater today\b/.test(text)) {
    const d = new Date(now);
    d.setHours(d.getHours() + 3, 0, 0, 0);
    if (d.getHours() >= 21) {
      d.setHours(20, 0, 0, 0);
    }
    return { date: d, recurrence };
  }

  if (/\btomorrow\b/.test(text)) {
    const base = new Date(now);
    base.setDate(base.getDate() + 1);
    return {
      date: atTime(base, time?.hour ?? DEFAULT_HOUR, time?.minute ?? 0),
      recurrence,
    };
  }

  if (/\btoday\b/.test(text) || /\btonight\b/.test(text)) {
    const hour = time?.hour ?? (/\btonight\b/.test(text) ? 20 : DEFAULT_HOUR);
    return { date: atTime(now, hour, time?.minute ?? 0), recurrence };
  }

  // Weekday name (with optional "next").
  const forceNext = /\bnext\b/.test(text);
  for (const [name, weekday] of Object.entries(WEEKDAYS)) {
    const re = new RegExp(`\\b${name}\\b`);
    if (re.test(text)) {
      const base = nextWeekday(now, weekday, forceNext);
      return {
        date: atTime(base, time?.hour ?? DEFAULT_HOUR, time?.minute ?? 0),
        recurrence,
      };
    }
  }

  // Bare time ("14:00") → today if still ahead, else tomorrow.
  if (time) {
    let d = atTime(now, time.hour, time.minute);
    if (d.getTime() <= now.getTime()) {
      d = new Date(d);
      d.setDate(d.getDate() + 1);
    }
    return { date: d, recurrence };
  }

  return null;
}

export function formatWhen(date: Date): string {
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
